import React from 'react';
import { FormikProps } from 'formik';
import { useSelector } from 'react-redux';
import Tooltip from '@cpg-its/ads.common.tooltip';
import { NewHireProps } from '../../../../models/arc-epi-props';
import { getCurrencyFormat } from '../../../../../../global/helpers/format-helpers';
import { ContentSpacer } from '../../../../../../global/components/layout/generic-content-spacer';
import { DividerSection } from '../../../../../../global/components/layout/divider-section';
import { summaryStyles } from '../../../../../../global/constants/theme/styles/pages/annual-enrollment/summary-section';
import { TcsmStates } from '../../../../../../root/tcsm-model';
import { CalculatedTiers } from '../../models/new-hire-enrollment-models';
import { getNameAndRateByCodeGroupLifeLongTernm, getRateByCodeAndTier } from '../../enrollment/helpers/coverage-display-helpers';
import { MemberCompletedCoverage, MemberPendingCoverage } from '../models/member-coverage-model';
import { CoveragePlans, DeclinedValue, PlanTypes } from '../../enrollment/models/coverage-models';
import { tcsmColors } from '../../../../../../global/constants/theme/theme-constants/colors';


interface Props {
    formikProps: FormikProps<any>;
    memberPendingPlans: MemberPendingCoverage;
    memberCompletedPlans: MemberCompletedCoverage;
    settings: NewHireProps;
}

interface GroupLifePlans {
    name: String;
    rate?: number;
}

const initialValues: GroupLifePlans = {
    name: '',
    rate: 0,
};


export function MemberPlanSummaryDisplay(props: Props) {
    const summaryClasses = summaryStyles();
    const { formikProps, memberPendingPlans, memberCompletedPlans, settings } = props;

	const verifyHealthPlansRate = (offeredPlan: CoveragePlans, planCode: string, selectedTier: string ) => {
		if (offeredPlan) {
			return getRateByCodeAndTier(offeredPlan?.plans, planCode, selectedTier);
		}

        if (!offeredPlan && memberCompletedPlans?.medical?.plan?.rate) {
            return memberCompletedPlans?.medical?.plan?.rate;
        }

        return 0;
    };

	
	const displayTierMedical = useSelector<TcsmStates, CalculatedTiers>(state => state?.newHireEnrollmentState?.tierToDisplayMedical);
	const displayTierDental = useSelector<TcsmStates, CalculatedTiers>(state => state?.newHireEnrollmentState?.tierToDisplayDental);

    const medicalPlanCode = formikProps.values[PlanTypes.M];
    const medicalPlanName = memberPendingPlans?.medical ? memberPendingPlans?.medical.plans.find(item => item.code === medicalPlanCode)?.name : memberCompletedPlans?.medical?.plan?.name;

    const dentalPlanCode = formikProps.values[PlanTypes.D];
    const dentalPlanName = memberPendingPlans?.dental ? memberPendingPlans?.dental.plans.find(item => item.code === dentalPlanCode)?.name : memberCompletedPlans?.dental?.plan?.name;

	const medicalAmount = memberCompletedPlans?.medical?.plan?.rate ?? verifyHealthPlansRate(memberPendingPlans?.medical, medicalPlanCode, displayTierMedical?.selected_tier);
	const dentalAmount = memberCompletedPlans?.dental?.plan?.rate ?? verifyHealthPlansRate(memberPendingPlans?.dental, dentalPlanCode, displayTierDental?.selected_tier);
	
	const groupLifePlan: GroupLifePlans = {
		name: memberCompletedPlans?.group_life?.plan?.name ?? DeclinedValue,
		rate: memberCompletedPlans?.group_life?.plan?.rate ?? 0,
	};

    /* LONG TERM PLANS ASSIGN LOGIC */
    let longTermPlan: GroupLifePlans = initialValues;

    if (memberPendingPlans?.long_term_disability) {
        const longternmPlanValue = formikProps?.values?.L;
        longTermPlan = getNameAndRateByCodeGroupLifeLongTernm(memberPendingPlans?.long_term_disability?.plans, longternmPlanValue);
    }

	if (memberCompletedPlans?.long_term_disability) {
		longTermPlan = {
			name: memberCompletedPlans?.long_term_disability?.plan?.name ?? DeclinedValue,
			rate: memberCompletedPlans?.long_term_disability?.plan?.rate ?? 0,
		};
	}


    /* SHORT TERM PLANS ASSIGN LOGIC */
    let shortTermPlan: GroupLifePlans = initialValues;

    if (memberPendingPlans?.short_term_disability) {
        const shortPlanValue = formikProps?.values?.W;
        shortTermPlan = getNameAndRateByCodeGroupLifeLongTernm(memberPendingPlans?.short_term_disability?.plans, shortPlanValue);
    }

	if (memberCompletedPlans?.short_term_disability) {
		shortTermPlan = {
			name: memberCompletedPlans?.short_term_disability?.plan?.name ?? DeclinedValue,
			rate: memberCompletedPlans?.short_term_disability?.plan?.rate ?? 0,
		};
	}

    /* UEI TERM PLANS ASSIGN LOGIC */

    let ueiPlan: GroupLifePlans = initialValues;

    if (memberPendingPlans?.UEI) {
        const ueiPlanValue = formikProps?.values?.U;
        ueiPlan = getNameAndRateByCodeGroupLifeLongTernm(memberPendingPlans?.UEI?.plans, ueiPlanValue);
    }

    if (memberCompletedPlans?.UEI) {
        ueiPlan = {
            name: memberCompletedPlans?.UEI?.plan?.name ?? '',
            rate: memberCompletedPlans?.UEI?.plan?.rate ?? 0,
        };
    }

	const hasMedical = !!memberCompletedPlans?.medical?.plan?.name || memberPendingPlans?.medical?.plans?.length > 0;
	const hasDental = !!memberCompletedPlans?.dental?.plan?.name || memberPendingPlans?.dental?.plans?.length > 0;
	const hasGroupLife = !!memberCompletedPlans?.group_life?.plan?.name;
	const hasLtd = !!memberCompletedPlans?.long_term_disability?.plan?.name || memberPendingPlans?.long_term_disability?.plans?.length > 0;
	const hasStd = !!memberCompletedPlans?.short_term_disability?.plan?.name || memberPendingPlans?.short_term_disability?.plans?.length > 0;
	const hasUei = !!memberCompletedPlans?.UEI?.plan?.name || memberPendingPlans?.UEI?.plans?.length > 0;

	return (
        <>
            <DividerSection removeDivider>
                <div className={summaryClasses.summarySection}>
                    <div className='summary'>
                        <h2 className='summary-header'>{settings.summarySectionTitle}</h2>
                        {
							hasMedical && (medicalPlanName || dentalPlanName || medicalPlanCode === DeclinedValue || dentalPlanCode === DeclinedValue) && (
                                <>
                                    <ContentSpacer>
                                        <h3>{settings?.monthlyCostLabel}</h3>
                                    </ContentSpacer>
                                    <div className='summary-header'>
                                        <div className='summary-tooltip'>
                                            <h4>{settings?.monthlyCostHealthLabel}</h4>
                                            <div className='summary-tooltip-inner'>
                                                <Tooltip iconColor={tcsmColors.blue} title={settings.monthlyCostHealthHelper} />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )
                        }
                        {
                            hasMedical && (medicalPlanName || medicalPlanCode === DeclinedValue) && (
                                <>
                                    {
                                        medicalAmount !== 0 ? (
                                            <div className='summary-row'>
                                                <p>{medicalPlanName}</p>
                                                <p>{getCurrencyFormat(medicalAmount)}</p>
                                            </div>
                                        ) : (
                                            <>
                                                <ContentSpacer>
                                                    <p>{settings?.declineMedicalCoverageMessage}</p>
                                                </ContentSpacer>
                                            </>
                                        )
                                    }
                                </>
                            )
                        }

                        {
                            hasDental && (dentalPlanName || dentalPlanCode === DeclinedValue) && (
                                <>
                                    {
                                        dentalAmount !== 0 ? (
                                            <div className='summary-row'>
                                                <p>{dentalPlanName}</p>
                                                <p>{getCurrencyFormat(dentalAmount)}</p>
                                            </div>
                                        ) : (
                                            <>
                                                <ContentSpacer>
                                                    <div className='summary-decline'>
                                                        <p>{settings?.declineDentalCoverageMessage}</p>
                                                    </div>
                                                </ContentSpacer>
                                            </>
                                        )
                                    }
                                </>
                            )
                        }

                        {
                            (medicalPlanName || dentalPlanName) && (
                                <div className='summary-subtotal'>
                                    <div className='summary-row'>
                                        <p>{settings.healthSubTotalLabel}</p>
                                        <p className='h4-styled'>{getCurrencyFormat((medicalAmount ?? 0) + (dentalAmount ?? 0))}</p>
                                    </div>
                                </div>
                            )
                        }

                        {
							hasGroupLife && groupLifePlan?.name && (
                                <div className='summary-header'>
                                    <div className='summary-tooltip'>
                                        <p className='h4-styled'>{settings.groupCostLabel}</p>
                                        <div className='summary-tooltip-inner'>
                                            <Tooltip iconColor={tcsmColors.blue} title={settings.groupCostHelper} />
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                        {
                            hasGroupLife && groupLifePlan?.name && (
                                <>
                                    {
                                        groupLifePlan?.rate !== 0 ? (
                                            <>
                                                <div className='summary-row'>
                                                    <p>{groupLifePlan?.name}</p>
                                                    <p>{getCurrencyFormat(groupLifePlan?.rate)}</p>
                                                </div>
                                                <div className='summary-subtotal'>
                                                    <div className='summary-row'>
                                                        <p>{settings.groupCostSubTotalLabel}</p>
                                                        <p className='h4-styled'>{getCurrencyFormat((groupLifePlan?.rate))}</p>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <ContentSpacer>
                                                    <div className='summary-decline'>
                                                        <p>{settings.groupLifeRateValueNotAvailable}</p>
                                                    </div>
                                                </ContentSpacer>
                                            </>
                                        )
                                    }
                                </>
                            )
                        }

                        {
                            (hasStd || hasLtd) && (longTermPlan?.name || shortTermPlan?.name) && (
                                <div className='summary-header'>
                                    <div className='summary-tooltip'>
                                        <p className='h4-styled'>{settings.disabilityCostLabel}</p>
                                        <div className='summary-tooltip-inner'>
                                            <Tooltip iconColor={tcsmColors.blue} title={settings.disabilityCostHelper} />
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                        {
                            hasStd && shortTermPlan?.name && (
                                <>
                                    {
                                        shortTermPlan?.rate !== 0 ? (
                                            <div className='summary-row' >

                                                <p>{shortTermPlan?.name}</p>
                                                <p>{getCurrencyFormat(shortTermPlan.rate)}</p>
                                            </div>
                                        ) : (
                                            <ContentSpacer>
                                                <div className='summary-decline'>
                                                    <p>{settings.declineStdCoverageMessage}</p>
                                                </div>
                                            </ContentSpacer>
                                        )
                                    }
                                </>
                            )
                        }

                        {
                            hasLtd && longTermPlan?.name && (
                                <>
                                    {
                                        longTermPlan?.rate !== 0 && longTermPlan?.name !== DeclinedValue ? (
                                            <>
                                                <div className='summary-row'>
                                                    <p>{longTermPlan?.name}</p>
                                                    <p>{getCurrencyFormat(longTermPlan?.rate)}</p>
                                                </div>
                                            </>
                                        ) : (
                                            <ContentSpacer>
                                                <div className='summary-decline'>
                                                    <p>{settings.declineLtdCoverageMessage}</p>
                                                </div>
                                            </ContentSpacer>
                                        )
                                    }
                                </>
                            )
                        }

                        {
                            (hasStd || hasLtd) && (longTermPlan?.name || shortTermPlan?.name) && (
                                <div className='summary-subtotal'>
                                    <div className='summary-row'>

                                        <p>{settings?.disabilitySubTotalLabel}</p>

                                        <p>{getCurrencyFormat((shortTermPlan?.rate ?? 0) + (longTermPlan?.rate ?? 0))}</p>
                                    </div>
                                </div>
                            )
                        }
                        {
                            hasUei && ueiPlan?.name && (
                                <div className='summary-header'>
                                    <div className='summary-tooltip'>
                                        <p className='h4-styled'>{settings?.ueiCostLabel}</p>
                                        <div className='summary-tooltip-inner'>
                                            <Tooltip iconColor={tcsmColors.blue} title={settings.ueiCostHelper} />
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                        {
                            hasUei && ueiPlan?.name && (
                                <>
                                    {
                                        ueiPlan?.rate !== 0 ? (
                                            <>
                                                <div className='summary-row'>
                                                    <p>{ueiPlan?.name}</p>
                                                    <p>{getCurrencyFormat(ueiPlan?.rate)}</p>
                                                </div>
                                                <div className='summary-subtotal'>
                                                    <div className='summary-row'>
                                                        <p>{settings.ueiSubTotalLabel}</p>
                                                        <p className='h4-styled'>{getCurrencyFormat((ueiPlan?.rate))}</p>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <ContentSpacer>
                                                    <div className='summary-decline'>
                                                        <p>{settings.declineUeiCoverageMessage}</p>
                                                    </div>
                                                </ContentSpacer>
                                            </>
                                        )
                                    }
                                </>
                            )
                        }
                        <div className='summary-total'>
                            <div className='summary-total-row'>
                                <p className='h3-styled'>{settings.totalCostLabel}</p>
                                <p className='h3-styled'>{getCurrencyFormat(dentalAmount + medicalAmount + groupLifePlan?.rate + shortTermPlan?.rate + longTermPlan?.rate + ueiPlan?.rate)}</p>
                            </div>
                        </div>
                        <p>{settings.totalCostDescription} </p>

                    </div>

                </div>
            </DividerSection>





        </>
    );
}